import { flash, Switch } from "@xola/ui-kit";
import React from "react";
import { useMutation } from "react-query";
import {
    Experience,
    ExperiencePurchaseItem,
    NotificationPreferenceType,
    Permission,
    Purchase,
    PurchaseItem,
    PurchaseItemObject,
    PurchaseItemReminderStatus,
    ReviewDestination,
} from "types";
import { IsGranted } from "../../Common/IsGranted";
import { api } from "@/api/api";
import { useExperience } from "@/hooks/resources/useProduct";
import { useInvalidate } from "@/hooks/useInvalidation";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface CollectCustomerReviewToggleProps {
    purchase: Purchase;
    item: PurchaseItem;
}

const getReviewReminder = (item: ExperiencePurchaseItem) => {
    return item.reminders?.find(
        (reminder) => reminder.type === NotificationPreferenceType.PURCHASE_REVIEW_REMINDER_TRAVELER,
    );
};

export const isReviewEnabled = (experience: Experience | undefined): boolean => {
    const destination = experience?.reviewPreference?.destination ?? getSeller().preferences.review?.destination;
    return !!destination && destination !== ReviewDestination.OFF;
};

export const CollectCustomerReviewToggle = ({
    purchase,
    item,
}: CollectCustomerReviewToggleProps): JSX.Element | null => {
    const invalidate = useInvalidate();
    const productId = item.object === PurchaseItemObject.EXPERIENCE ? item.product?.id : undefined;
    const { experience, isLoadingExperience } = useExperience(productId, null, { enabled: !!productId });

    const { mutateAsync: updateCollectReview, isLoading } = useMutation(
        async (collectReview: boolean) => {
            const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, {
                items: [
                    {
                        id: item.id,
                        collectReview,
                    },
                ],
            });
            return data;
        },
        {
            onSuccess: (data) => {
                updateCache("purchases", data);
                invalidate("purchases", purchase.id);
            },
            onError: () => {
                flash.error("Failed to update review collection setting");
            },
        },
    );

    if (item.object !== PurchaseItemObject.EXPERIENCE) {
        return null;
    }

    const reviewReminder = getReviewReminder(item as ExperiencePurchaseItem);
    if (!reviewReminder || isLoadingExperience || !isReviewEnabled(experience)) {
        return null;
    }

    const isChecked = item.collectReview ?? true;
    const isReminderActive = reviewReminder.status === PurchaseItemReminderStatus.ACTIVE;

    return (
        <IsGranted permissions={Permission.MODIFY_BOOKING_LITE}>
            <div data-testid="collect-customer-review-toggle">
                <Switch.Group>
                    <Switch
                        isChecked={isChecked}
                        disabled={isLoading || !isReminderActive}
                        size="medium"
                        onChange={updateCollectReview}
                    />
                    <Switch.Label direction="right">Collect customer review</Switch.Label>
                </Switch.Group>
            </div>
        </IsGranted>
    );
};
